import React, { forwardRef } from 'react';
import { Box, Typography, Button } from '@mui/material';

const SEARCH_ICON_URL =
  'https://assets.askhippo.co.za/Icons/cross-sell/search.svg';

const CROSS_SELL_ICON_BACKGROUND_URL =
  'https://assets.askhippo.co.za/Vectors/CrossSellIconBackgroundVector.svg';

const BASE = 'https://assets.askhippo.co.za/Icons/cross-sell/';

const ICON_MAP = {
  'Car Insurance': `${BASE}car-insurance.svg`,
  'Building Insurance': `${BASE}building-insurance.svg`,
  'Home Contents': `${BASE}home-contents.svg`,
  'Home Contents Insurance': `${BASE}home-contents.svg`,
  'Life Insurance': `${BASE}life-insurance.svg`,
  'Funeral Insurance': `${BASE}funeral-cover.svg`,
  'Motor Warranty': `${BASE}motor-warranty.svg`,
  'Motor Warranty Insurance': `${BASE}motor-warranty.svg`,
  'Service Plan': `${BASE}service-plans.svg`,
  'Service Plans': `${BASE}service-plans.svg`,
  'Car Service Plan': `${BASE}service-plans.svg`,
  'Tyre & Rim': `${BASE}tyre-and-rim.svg`,
  'Tyre and Rim': `${BASE}tyre-and-rim.svg`,
  'Tyre & Rim Insurance': `${BASE}tyre-and-rim.svg`,
  'Tyre and Rim Insurance': `${BASE}tyre-and-rim.svg`,
  'Tyre & Rim Guard': `${BASE}tyre-and-rim.svg`,
  'Medical Aid': `${BASE}medical-aid.svg`,
  Fibre: `${BASE}home-fibre.svg`,
  'Vehicle Tracking': `${BASE}vehicle-tracking.svg`,
  'Vehicle Tracking Insurance': `${BASE}vehicle-tracking.svg`,
};

const ProviderTileComponentCrossSellCarousel = forwardRef(
  ({ data, onClick, isMobile, reserveBannerSpace = false }, ref) => {
    const {
      TotalPremium,
      DisplayAmount,
      DisplayAmountCaption = 'From',
      DisplayAmountPrefix = 'R',
      DisplayAmountSuffix = 'pm',
      productCategory,
    } = data;
    const amountValue = Number(DisplayAmount ?? TotalPremium);
    const hasAmount = Number.isFinite(amountValue);
    const amountText = hasAmount
      ? `${DisplayAmountPrefix}${Math.round(amountValue).toLocaleString(
          'en-ZA'
        )}`
      : 'View';

    const productIcon = ICON_MAP[productCategory];

    return (
      <Box
        ref={ref}
        sx={{
          width: isMobile ? '317px' : '389px',
          height: '201px',
          borderRadius: '22px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 5px 15px 0px #00000026',
          border: data.bestDeal ? '2px solid #2DC8CB' : 'none',
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-start',
          padding: '0 16px 0 290px',
          overflow: 'hidden',
          transition: 'height 0.3s ease',
        }}
      >
        {data.bestDeal && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              backgroundColor: '#2DC8CB',
              borderTopLeftRadius: '18px',
              borderTopRightRadius: '18px',
              alignItems: 'center',
              paddingRight: '24px',
              boxSizing: 'border-box',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1.250rem',
                lineHeight: '24px',
                letterSpacing: '-0.03em',
                color: '#FFFFFF',
                textAlign: 'center',
                mt: '4px',
                mb: '4px',
              }}
            >
              Your Best Deal!
            </Typography>
          </Box>
        )}

        {!data.bestDeal &&
          Array.isArray(data.RetailChips) &&
          data.RetailChips.map((chip, idx) => (
            <Box
              key={idx}
              sx={{
                right: '16px',
                top: '16px',
                position: 'absolute',
                fontSize: '0.75rem',
                fontWeight: 600,
                color: '#162F3B',
                backgroundColor: chip.color,
                letterSpacing: '-0.02em',
                lineHeight: '1.125rem',
                borderRadius: '6px',
                px: '8px',
                py: '2px',
                display: 'inline-block',
                mb: '1px',
              }}
            >
              {chip.text}
            </Box>
          ))}

        {/* Icon + Name Row */}
        <Box
          sx={{
            position: 'absolute',
            top: data.bestDeal || reserveBannerSpace ? '44px' : '16px',
            left: '16px',
            right: '16px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '68px',
              height: '68px',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={CROSS_SELL_ICON_BACKGROUND_URL}
              alt="Cross-sell icon background"
              sx={{ width: '100%', height: '100%' }}
            />
            {productIcon && (
              <Box
                component="img"
                src={productIcon}
                alt={`${productCategory} icon`}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '36px',
                  height: '36px',
                }}
              />
            )}
          </Box>
          <Typography
            sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1.375rem',
              letterSpacing: '-0.02em',
              color: '#1E354E',
            }}
          >
            {productCategory}
          </Typography>
        </Box>

        {/* Divider */}
        <Box
          sx={{
            position: 'absolute',
            bottom: '92px',
            left: '16px',
            right: '16px',
            height: '1px',
            backgroundColor: '#E0E0E0',
          }}
        />

        {/* Premium Section */}
        <Box
          sx={{
            position: 'absolute',
            left: '16px',
            bottom: '12px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 400,
                color: '#A2ACB1',
                mb: '-5px',
                ml: '2px',
              }}
            >
              {hasAmount ? DisplayAmountCaption : 'Quote'}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 700,
                  fontSize: '3rem',
                  lineHeight: '120%',
                  letterSpacing: '-0.02em',
                  color: '#1E354E',
                }}
              >
                {amountText}
              </Typography>
              {hasAmount && DisplayAmountSuffix && (
                <Typography
                  sx={{
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    ml: '2px',
                    color: '#1E354E',
                  }}
                >
                  {DisplayAmountSuffix}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Button
          onClick={onClick}
          sx={{
            position: 'absolute',
            right: '16px',
            bottom: '16px',
            backgroundColor: '#FF0074',
            borderRadius: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 0,
            padding: '12px',
          }}
        >
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <Box
              component="img"
              src={SEARCH_ICON_URL}
              alt="Search"
              sx={{ width: 24, height: 24, m: 0.5 }}
            />
          </Box>
        </Button>
      </Box>
    );
  }
);

ProviderTileComponentCrossSellCarousel.displayName =
  'ProviderTileComponentCrossSellCarousel';
export default ProviderTileComponentCrossSellCarousel;
